import {Map} from "immutable";
import * as React from "react";
import {connect, useDispatch} from "react-redux";
import {
    hasHoldings,
    investmentsEnabled,
    isEditingPlanPortfolio,
    portfolioProductSummariesSelector,
    productUniverseSummariesSelector,
    userType,
} from "../../../mainReducerMapSelectors";
import {getBrowserLocationPath} from "../../api/BrowserWrapper";
import {ProductSummary} from "../../model/product/ProductSummary";
import {ResearchSearchItem} from "../../model/Search.model";
import {getSearchItemsFromProducts} from "../../utils/researchUtil";
import {hasConsultantLevelAccess} from "../../utils/sessionUtil";
import {
    allResearchItems,
    investmentsItem,
    IResearchNavigationMenuItems,
    researchItem,
    ResearchMenuItems,
} from "../base/NavItems";
import SubNav from "../base/SubNav.component";
import {getSuccessData} from "../common/commonStates";
import {UserTypeEnum} from "../../model/UserInfo.model";
import {IApplicationRootState} from "../../../applicationState";
import {showInvestments} from "../../utils/navigationUtils";
import {getTranslation} from "../../utils/translationUtil";
import {useEffect} from "react";
import {getAllProducts, getPortfolioProductSummaries} from "../product/AllProductsActions";
import {useNavigate} from "react-router-dom";
import {multipleApiRequesterWrapper} from "../common/MultipleApiRequesterWrapper";
import {navigateTo} from "../../../navigateTo";
import {AnyThunkDispatch} from "../../../commonTypes";

interface IResearchNavPropsFromState {
    editingPlanPortfolio: boolean;
    hasHoldings: boolean;
    productUniverseSummaries: Map<number, ProductSummary> | null;
    portfolioProductSummaries: Map<number, ProductSummary> | null;
    userType: UserTypeEnum;
    investmentsEnabled: boolean;
}

export type ResearchNavProps = IResearchNavPropsFromState;

export const researchDescription = "Explore Aon's comprehensive views on investment managers and strategies. " +
    "Research and gain insights into the various investment opportunities available.";

export const ResearchNavComponent: React.FunctionComponent<ResearchNavProps> = (props) => {
    const menuItems = getNavigationMenuItems();
    const dispatch: AnyThunkDispatch = useDispatch();
    const navigate = useNavigate();

    function getResearchMenuItemFromUrl() {
        const maybeMenuItem =
            allResearchItems.find((item) => getBrowserLocationPath().includes(item.targetUrl));
        return maybeMenuItem ? maybeMenuItem.id : researchItem.id;
    }

    function getNavigationMenuItems(): IResearchNavigationMenuItems[] {
        const maybeInvestmentsItem = showInvestments(props.hasHoldings, props.userType, props.investmentsEnabled)
            ? investmentsItem
            : undefined;

        return [maybeInvestmentsItem, researchItem]
            .filter((item) => !!item) as IResearchNavigationMenuItems[];
    }

    function isSearchEnabled() {
        const isResearchPage = getBrowserLocationPath().includes("/research");
        return isResearchPage
            ? props.editingPlanPortfolio && getResearchMenuItemFromUrl() === ResearchMenuItems.RESEARCH_ID.toString()
            : getResearchMenuItemFromUrl() === ResearchMenuItems.RESEARCH_ID.toString();
    }

    function onItemSelected(selectedMenuItem: string) {
        navigateTo(
            menuItems.find((item) => item.id === selectedMenuItem)!.targetUrl,
            navigate
        );
    }

    function getSearchItems(): ResearchSearchItem[] {
        const productSummaries = (hasConsultantLevelAccess() && props.editingPlanPortfolio)
            ? props.productUniverseSummaries?.valueSeq().toArray()
            : props.portfolioProductSummaries?.valueSeq().toArray();

        return productSummaries ? getSearchItemsFromProducts(productSummaries) : [];
    }

    useEffect(() => {
        if (hasConsultantLevelAccess() && props.editingPlanPortfolio && !props.productUniverseSummaries) {
            dispatch(getAllProducts);
        } else {
            if (!props.portfolioProductSummaries) {
                dispatch(getPortfolioProductSummaries);
            }
        }
    }, [props.editingPlanPortfolio]);

    const renderResearchHeader = () => {
        const isNewResearchPage = getBrowserLocationPath().includes("/research");
        return isNewResearchPage
            ? <div className="sub-nav__research-header-container" data-testid="sub-nav__research-header-container">
                <div className="sub-nav__research-header-title">{getTranslation("subnav.researchItem", "Research")}</div>
                <div className="sub-nav__research-header-description">
                    {getTranslation("research-page.research.description", researchDescription)}
                </div>
            </div>
            : null;
    };

    return <>
        <SubNav
            menuItems={menuItems}
            searchEnabled={isSearchEnabled()}
            onItemSelected={onItemSelected}
            selectedMenuItem={getResearchMenuItemFromUrl()}
            searchItems={getSearchItems()}
        />
        {!props.editingPlanPortfolio && renderResearchHeader()}
    </>;
};

export const mapStateToProps = (state: IApplicationRootState): IResearchNavPropsFromState => {
    return {
        editingPlanPortfolio: isEditingPlanPortfolio(state),
        hasHoldings: hasHoldings(state),
        productUniverseSummaries: getSuccessData(productUniverseSummariesSelector(state))!,
        portfolioProductSummaries: getSuccessData(portfolioProductSummariesSelector(state))!,
        userType: userType(state),
        investmentsEnabled: investmentsEnabled(state),
    };
};

// export default withRouter(connect<IResearchNavPropsFromState>(mapStateToProps)(ResearchNavComponent));
// export default connect<IResearchNavPropsFromState>(mapStateToProps)(ResearchNavComponent);

export default multipleApiRequesterWrapper(connect<IResearchNavPropsFromState>(mapStateToProps)(ResearchNavComponent), []);
